import { Plus, Trash2 } from 'lucide-react';
import { Button, Input } from './ui';

export interface DialPatternRow {
  pattern: string;
  strip: number;
  prepend: string;
}

/**
 * A table of dial patterns, each with its own digit manipulation.
 *
 * A table rather than one field per route because strip and prepend belong to the pattern: "9."
 * strips one digit and "1NXXNXXXXXX" strips none, and they are the same route to whoever set them
 * up. One strip for the whole route forces that into two routes with duplicated trunk lists, and
 * the duplicates are what rot.
 */
export function PatternRows({
  rows,
  onChange,
  withDigits = true,
  placeholder = 'X.',
}: {
  rows: DialPatternRow[];
  onChange: (next: DialPatternRow[]) => void;
  /** Inbound patterns match only; outbound ones also transform. */
  withDigits?: boolean;
  placeholder?: string;
}) {
  const update = (index: number, patch: Partial<DialPatternRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <div className="rounded-[--radius] border border-border">
      <div
        className={
          withDigits
            ? 'grid grid-cols-[2fr_1fr_1fr_auto] gap-2 border-b border-border px-3 py-2 text-xs font-medium text-muted-foreground'
            : 'grid grid-cols-[1fr_auto] gap-2 border-b border-border px-3 py-2 text-xs font-medium text-muted-foreground'
        }
      >
        <span>Pattern</span>
        {withDigits && <span>Strip</span>}
        {withDigits && <span>Prepend</span>}
        <span className="w-8" />
      </div>

      {rows.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-muted-foreground">
          No patterns. Add at least one, or this route matches nothing.
        </p>
      ) : (
        rows.map((row, index) => (
          <div
            key={index}
            className={
              withDigits
                ? 'grid grid-cols-[2fr_1fr_1fr_auto] items-center gap-2 border-b border-border/60 px-3 py-2 last:border-0'
                : 'grid grid-cols-[1fr_auto] items-center gap-2 border-b border-border/60 px-3 py-2 last:border-0'
            }
          >
            <Input
              value={row.pattern}
              onChange={(e) => update(index, { pattern: e.target.value })}
              placeholder={placeholder}
              className="h-8 font-mono"
            />
            {withDigits && (
              <Input
                type="number"
                min={0}
                max={20}
                value={row.strip}
                onChange={(e) => update(index, { strip: Number(e.target.value) })}
                className="h-8"
              />
            )}
            {withDigits && (
              <Input
                value={row.prepend}
                onChange={(e) => update(index, { prepend: e.target.value })}
                className="h-8 font-mono"
              />
            )}
            <Button
              variant="ghost"
              className="h-8 w-8 px-0 text-destructive"
              aria-label="Remove pattern"
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))
      )}

      <button
        type="button"
        onClick={() => onChange([...rows, { pattern: '', strip: 0, prepend: '' }])}
        className="flex w-full items-center justify-center gap-1.5 py-2 text-sm text-muted-foreground hover:bg-accent"
      >
        <Plus className="size-4" />
        Add
      </button>
    </div>
  );
}
